import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import TypeWriter from '../components/TypeWriter';
import "./Learn.css";

const Learn = () => {
  const [completed, setCompleted] = useState(new Set());
  const [expanded, setExpanded] = useState({
    sign: true,
    morse: false,
    tryIt: false,
    quiz: false,
  });

  const navigate = useNavigate();

  const signResources = [
    { label: "GitHub: Sign Language Projects", url: "https://github.com/topics/sign-language" },
    { label: "ASL Open Educational Resources", url: "https://asccc-oeri.org/open-educational-resources-and-american-sign-language/" },
    { label: "LibHunt: Top Sign Language Tools", url: "https://www.libhunt.com/topic/sign-language" },
    { label: "Sign Language Interpreter (Live Video)", url: "https://github.com/harshbg/Sign-Language-Interpreter-using-Deep-Learning" },
    { label: "Sign Language to Text Conversion", url: "https://github.com/emnikhil/Sign-Language-To-Text-Conversion" },
    { label: "Indian Sign Language Recognition", url: "https://github.com/imRishabhGupta/Indian-Sign-Language-Recognition" },
    { label: "YouTube: BEST resources for learning ASL", url: "https://www.youtube.com/watch?v=2NwpANvcbBI" },
    { label: "YouTube: Learn This FIRST!!! (10 Things)", url: "https://www.youtube.com/watch?v=6w1ZDaE-whc" },
    { label: "YouTube: Sign Language for Beginners", url: "https://www.youtube.com/watch?v=xAQ9Pegg0gc" },
    { label: "YouTube: Top Sign Language Resources", url: "https://www.youtube.com/watch?v=HOR1bVyCNjQ" },
    { label: "YouTube: Indian Sign Language 101", url: "https://www.youtube.com/watch?v=ZE7EFgd0IWs" },
  ];

  const morseResources = [
    { label: "GitHub: Morse Code Repositories", url: "https://github.com/topics/morse-code" },
    { label: "Morse Code Ninja: Practice Tools", url: "https://morsecode.ninja/resources/" },
    { label: "LibHunt: Morse Code Projects", url: "https://www.libhunt.com/topic/morse-code" },
    { label: "Morse Code Translator (Audio + Text)", url: "https://github.com/ozdemirburak/morse-code-translator" },
    { label: "Neural Morse Decoder", url: "https://github.com/f4exb/morseangel" },
    { label: "Morse Learn Web App", url: "https://github.com/googlecreativelab/morse-learn" },
    { label: "YouTube: Morse Code Class 01", url: "https://www.youtube.com/watch?v=LR0NESR3tJY" },
    { label: "YouTube: Learn from a MEMORY CHAMP", url: "https://www.youtube.com/watch?v=D8tPkb98Fkk" },
    { label: "YouTube: 7 GREAT CW Learning Sites", url: "https://www.youtube.com/watch?v=Asyb6qJ9P7s" },
    { label: "YouTube: Learn Morse Code The Right Way", url: "https://www.youtube.com/watch?v=YJixhiExOcs" },
    { label: "YouTube: Improve Memorization!", url: "https://www.youtube.com/watch?v=spzSKvQWQoo" },
    { label: "YouTube: What Is Morse Code", url: "https://www.youtube.com/watch?v=N-KcLgXQVa4" },
  ];

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("learn_completed_urls") || "[]");
    setCompleted(new Set(saved));
  }, []);

  const toggleSection = (key) => {
    setExpanded({ ...expanded, [key]: !expanded[key] });
  };

  const markCompleted = (url) => {
    if (completed.has(url)) return;
    const updated = new Set(completed);
    updated.add(url);
    setCompleted(updated);
    localStorage.setItem("learn_completed_urls", JSON.stringify(Array.from(updated)));
    
    // Add a small delay to ensure smooth animation
    setTimeout(() => {
      // Trigger any additional effects if needed
      const completedElements = document.querySelectorAll('.resource-row.completed');
      completedElements.forEach((element, index) => {
        element.style.animationDelay = `${index * 0.1}s`;
      });
    }, 50);
  };

  const openLink = (url) => {
    window.open(url, "_blank");
    markCompleted(url);
  };

  const getIcon = (url) => {
    if (url.includes("github.com")) return { cls: 'github', glyph: '🔗' };
    if (url.includes("youtube.com")) return { cls: 'youtube', glyph: '▶️' };
    return { cls: 'doc', glyph: '📘' };
  };

  const renderResources = (resources) =>
    resources.map((r, i) => {
      const isDone = completed.has(r.url);
      const icon = getIcon(r.url);
      return (
        <div key={i} className={`resource-row ${isDone ? 'completed' : ''}`} onClick={() => openLink(r.url)}>
          <div className={`checkbox-circle ${isDone ? 'checked' : ''}`} />
          <span className={`resource-icon ${icon.cls}`}>{icon.glyph}</span>
          <span className="resource-label">{r.label}</span>
        </div>
      );
    });

  const renderSection = (title, resources, key) => {
    const completedCount = resources.filter((r) => completed.has(r.url)).length;
    return (
      <div className="learn-card">
        <div className="card-header" onClick={() => toggleSection(key)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ margin: 0 }}>{title}</h2>
            <div className="progress-badge">✅ {completedCount}/{resources.length}</div>
          </div>
          <span className="card-toggle">{expanded[key] ? '▲' : '▼'}</span>
        </div>
        {expanded[key] && (
          <div className="card-body">
            {renderResources(resources)}
            <div className="progress-indicator">
              ✅ Progress: {completedCount}/{resources.length} resources completed
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="learn-page">
      <div className="learn-header card" data-aos="fade-up" data-aos-duration="900">
        <h1>
          <TypeWriter 
            text="Learn with SignCrypt" 
            speed={85} 
            cursorColor="white"
          />
        </h1>
        <p>Explore Sign Language & Morse Code resources and track your progress</p>
      </div>

      <div className="learn-cards-container">
        {renderSection(" Learn Sign Language", signResources, "sign")}
        {renderSection(" Learn Morse Code", morseResources, "morse")}

        <div className="learn-card">
        <div className="card-header" onClick={() => toggleSection("tryIt")}>
          <h2>🛠 Try It Yourself</h2>
          <span>{expanded.tryIt ? "▲" : "▼"}</span>
        </div>
        {expanded.tryIt && (
          <div className="card-body">
            <p>
              Jump into GestureSpeak’s demo hub to try Morse and Sign input using real-time gesture recognition.
            </p>
            <button className="action-btn" onClick={() => navigate('/signal-hub')}>
              Open Demo Hub
            </button>
          </div>
        )}
      </div>

        <div className="learn-card">
        <div className="card-header" onClick={() => toggleSection("quiz")}>
          <h2>Test Your Knowledge</h2>
          <span>{expanded.quiz ? "▲" : "▼"}</span>
        </div>
        {expanded.quiz && (
          <div className="card-body">
            <p>
              Challenge yourself with quick quizzes on Morse and Sign Language. Earn badges and track your progress!
            </p>
            <button className="action-btn" onClick={() => navigate('/quiz')}>
              Start Quiz
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Learn;
